import { IntlShape } from 'react-intl';
import { ButtonProps } from '@designsystem/stories/02-atoms/Button';
import { InfoItemProps } from '@designsystem/stories/02-atoms/Info';
import { RegisterContent, TypeNormalizer } from '../types';
import { stringOrUndefined } from '../utils';
import { normalizeContent, normalizeLink } from '../normalizeContent';
import { messages } from '../messages';
import { normalizeEventCard } from './EventCardAdapter';
import dateFormat from '@fhnw/components/DateFormat/dateFormat';
import { downloadICalEvent, ICalEvent } from '@fhnw/helpers/Calendar';
import { pushAnalyticsPageview } from '@fhnw/components/DataLayerAnalytics/analytics';

const REGISTRATION_ANALYTICS_PUSHED = '__fhnwRegistrationAnalyticsPushed';

const getNativeAnalyticsEvent = (event?: any) =>
  event?.nativeEvent || event || undefined;

export const pushRegistrationAnalytics = (
  item: RegisterContent,
  event?: any,
) => {
  const nativeEvent = getNativeAnalyticsEvent(event);

  if (nativeEvent?.[REGISTRATION_ANALYTICS_PUSHED]) {
    return;
  }

  if (nativeEvent) {
    nativeEvent[REGISTRATION_ANALYTICS_PUSHED] = true;
  }

  pushAnalyticsPageview(item?.parent, {
    pathname: item?.parent?.['@id'],
    occasion: item,
  });
};

/**
 * Transforms register-type content (Occasion)
 */
export const normalizeRegisterCard: TypeNormalizer = (
  item,
  normalized,
  intl,
  university,
  imageSizes,
  onAnmelden,
) => {
  const registerItem = item as RegisterContent;

  // Set occasion title as main title
  normalized.title = registerItem?.occasion_title || normalized.title;

  // If there's a parent event, use its title and set occasion as subtitle
  if (registerItem?.parent) {
    const eventCard = createEventReference(registerItem.parent, intl);
    normalized.subtitle = normalized.title;
    normalized.title = eventCard.title || normalized.title;
  }

  // Build information section
  const info = buildRegisterInfo(registerItem, intl);
  if (info.length > 0) {
    normalized.info = info;
  }

  // Build action buttons
  const actions = buildRegisterActions(
    registerItem,
    intl,
    normalized,
    onAnmelden,
  );
  if (actions.length > 0) {
    normalized.actions = actions;
  }

  // Remove image and link for register cards
  delete normalized.image;
  delete normalized.link;

  return normalized;
};

/**
 * Transforms register-type content (Occasion)
 */
export const normalizeRegisterEventCard: TypeNormalizer = (
  item,
  normalized,
  intl,
  university,
  imageSizes,
  onAnmelden,
) => {
  const registerCard = normalizeRegisterCard(
    item,
    normalized,
    intl,
    university,
    imageSizes,
    onAnmelden,
  );

  normalized = normalizeEventCard(
    item,
    normalized,
    intl,
    university,
    imageSizes,
  );

  const actions: ButtonProps[] = [];

  if (normalized.link) {
    actions.push({
      type: 'tertiary',
      label: intl.formatMessage(messages.Details),
      href: normalized.link,
    });
  }

  // Forward the registration action (panel trigger or external link), but NOT the
  // plain "no registration required" info text (type:'plain'/div) — it is not a
  // button and must not render as a tertiary action on the event card.
  const firstAction = registerCard.actions?.[0];
  if (
    firstAction &&
    firstAction.type !== 'plain' &&
    !firstAction.div &&
    !firstAction.disabled
  ) {
    actions.push({
      ...firstAction,
      type: 'tertiary',
      className: 'btn--end',
    });
  }

  normalized.actions = actions;

  return normalized;
};

/**
 * Creates a reference to the parent event
 */
const createEventReference = (parent: any, intl: IntlShape) => {
  const normalizedContent = normalizeContent(parent, true);

  if (typeof normalizedContent === 'string') {
    return { title: parent.title };
  }

  const contentObj = normalizedContent as any;
  return {
    title: contentObj?.teaser_title || contentObj?.title || parent.title,
  };
};

/**
 * Builds information section for register cards
 */
const buildRegisterInfo = (
  item: RegisterContent,
  intl: IntlShape,
): InfoItemProps[] => {
  const info: InfoItemProps[] = [];

  // Date information
  if (item?.occasion_start || item?.occasion_end) {
    const startDate = item?.occasion_start
      ? new Date(item.occasion_start)
      : undefined;
    const endDate = item?.occasion_end
      ? new Date(item.occasion_end)
      : undefined;

    info.push({
      key: intl.formatMessage(messages.Date),
      value: dateFormat({
        start: startDate,
        end: endDate,
        openEnd: item?.open_end,
        wholeDay: item?.whole_day,
        locale: intl.locale,
      }),
    });
  }

  // Rolling intake
  if (item?.rolling_intake) {
    info.push({
      key: intl.formatMessage(messages.Date),
      value: intl.formatMessage(messages.RollingIntake),
    });
  }

  // Duration
  if (item?.duration) {
    info.push({
      key: intl.formatMessage(messages.Duration),
      value: item.duration,
    });
  }

  // School days - handle flexible types
  if (item?.school_days) {
    let schoolDaysValue: string | undefined;

    if (Array.isArray(item.school_days)) {
      // Handle array of strings or objects
      const stringArray = item.school_days.map((day) =>
        typeof day === 'string' ? day : day?.title || String(day),
      );
      schoolDaysValue = stringOrUndefined(stringArray, intl);
    } else if (typeof item.school_days === 'string') {
      schoolDaysValue = item.school_days;
    }

    if (schoolDaysValue) {
      info.push({
        key: intl.formatMessage(messages.SchoolDays),
        value: schoolDaysValue,
      });
    }
  }

  // Place
  if (item?.place) {
    info.push({
      key: intl.formatMessage(messages.Place),
      value: item.place,
    });
  }

  // Course management
  if (item?.course_management) {
    info.push({
      key: intl.formatMessage(messages.CourseManagement),
      value: item.course_management,
    });
  }

  // Application dates
  if (item?.application_start) {
    const startDate = new Date(item.application_start);
    info.push({
      key: intl.formatMessage(messages.ApplicationStart),
      value: dateFormat({
        start: startDate,
        dateOnly: true,
        locale: intl.locale,
      }),
    });
  }

  if (item?.final_application_date) {
    const finalDate = new Date(item.final_application_date);
    info.push({
      key: intl.formatMessage(messages.FinalApplicationDate),
      value: dateFormat({
        start: finalDate,
        dateOnly: true,
        locale: intl.locale,
      }),
    });
  }

  if (item?.total_places) {
    info.push({
      key: intl.formatMessage(messages.NumberOfSeats),
      value: item.total_places,
    });
  }

  if (item?.free_places) {
    info.push({
      key: intl.formatMessage(messages.AvailableSeats),
      value: item.free_places,
    });
  }

  // Additional information
  if (item?.additional_information) {
    info.push({
      key: intl.formatMessage(messages.Notes),
      value: item.additional_information,
    });
  }

  return info;
};

/**
 * Builds action buttons for register cards
 */
export const buildRegisterActions = (
  item: RegisterContent,
  intl: IntlShape,
  normalized: any,
  onAnmelden?: (item: RegisterContent) => void,
): ButtonProps[] => {
  const actions: ButtonProps[] = [];
  const now = new Date();
  const applicationStart = item?.application_start
    ? new Date(item.application_start)
    : null;
  const applicationEnd = item?.final_application_date
    ? new Date(item.final_application_date)
    : null;
  const occasionStart = item?.occasion_start
    ? new Date(item.occasion_start)
    : null;
  const isBeforeStart = !!(applicationStart && applicationStart > now);
  const isAfterEnd = !!(applicationEnd && applicationEnd < now);
  const isAfterOccasion = !!(occasionStart && occasionStart < now);

  const usesPanel = !!(
    (item?.cms_application && item?.cms_form_path) ||
    (item?.crm_application && item?.crm_embed_code)
  );
  const hasExternal = !!item?.application_button_link;
  const hasRegistration = usesPanel || hasExternal;

  if (
    item?.no_registration_required &&
    !hasRegistration &&
    !item?.participation_link
  ) {
    actions.push({
      type: 'plain',
      label: intl.formatMessage(messages.NoRegistrationRequired),
      div: true,
    });
  } else if (hasRegistration) {
    let label = intl.formatMessage(messages.Register);
    if (isBeforeStart) {
      label = intl.formatMessage(messages.RegisterFrom, {
        date: dateFormat({
          start: applicationStart!,
          dateOnly: true,
          locale: intl.locale,
        }),
      });
    } else if (item?.waiting_list) {
      label = intl.formatMessage(messages.WaitingList);
    }
    const disabled =
      isBeforeStart || isAfterEnd || isAfterOccasion || undefined;
    if (usesPanel) {
      actions.push({
        type: 'primary',
        label,
        className: 'ga-df-anmeldung',
        disabled,
        'data-analytics-occasion-id': item?.['@id'] ?? '',
        onClick: (event: any) => {
          event?.preventDefault?.();
          pushRegistrationAnalytics(item, event);
          if (!disabled) onAnmelden?.(item);
        },
      });
    } else {
      const link = normalizeLink(item.application_button_link as string);
      actions.push({
        type: 'primary',
        label,
        href: link,
        target: item?.new_tab ? '_blank' : '_self',
        className: 'ga-df-anmeldung',
        disabled,
        'data-analytics-occasion-id': item?.['@id'] || link,
        onClick: (event: any) => pushRegistrationAnalytics(item, event),
      });
    }
  }

  // Teilnahme button (cases 3 & 4) — independent of registration, always
  // primary: two yellow buttons when shown alongside Anmelden (#1664 mockup).
  if (item?.participation_link) {
    actions.push({
      type: 'primary',
      label: intl.formatMessage(messages.Participate),
      href: normalizeLink(item.participation_link),
      target: item?.participation_new_tab ? '_blank' : '_self',
    });
  }

  // Calendar import button
  if (shouldShowCalendarButton(item, now)) {
    actions.push(createCalendarButton(item, intl, normalized));
  }

  return actions;
};

/**
 * Checks if calendar button should be shown
 */
const shouldShowCalendarButton = (
  item: RegisterContent,
  now: Date,
): boolean => {
  const occasionStart = item?.occasion_start
    ? new Date(item.occasion_start)
    : null;
  return !!(
    occasionStart &&
    occasionStart > now &&
    (item?.parent?.['@type'] === 'FHNWEvent' ||
      item?.parent?.['@type'] === 'InfoEvent')
  );
};

/**
 * Creates calendar import button
 */
const createCalendarButton = (
  item: RegisterContent,
  intl: IntlShape,
  normalized: any,
): ButtonProps => {
  return {
    type: 'secondary',
    icon: 'time_limited_schedule_calendar',
    className: 'btn--end',
    title: intl.formatMessage(messages.ImportToCalendar),
    onClick: (e: React.MouseEvent<HTMLButtonElement> | MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const startTime = item.occasion_start
        ? new Date(item.occasion_start).toISOString()
        : '';
      const endTime = item.occasion_end
        ? new Date(item.occasion_end).toISOString()
        : undefined;

      let description = [];
      if (item?.parent?.teaser_text) {
        description.push(item?.parent?.teaser_text);
      } else if (item?.parent?.description) {
        description.push(item?.parent?.description);
      }
      if (item?.additional_information) {
        description.push(item.additional_information);
      }
      if (item?.parent?.['@id']) {
        description.push(item?.parent?.['@id']);
      }

      const event: ICalEvent = {
        title:
          normalized.title +
          (normalized.subtitle ? ` - ${normalized.subtitle}` : ''),
        description: description.join('\\n\\n'),
        startTime,
        endTime,
        wholeDay: item?.whole_day || false,
        location: item?.place?.replaceAll('\n', '\\n') || '',
        url: item?.parent?.['@id'],
      };

      downloadICalEvent(event, `${item?.['@id']?.split('/').pop()}.ics`);
    },
  };
};
